import { useEffect, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Loader2,
  Sparkles,
} from "lucide-react";
import {
  type CockpitInstantInsight,
  getApiErrorDetail,
  getCockpitInsight,
} from "@/lib/api";

interface Props {
  caseId: number;
}

export function InstantInsightPanel({ caseId }: Props) {
  const [insight, setInsight] = useState<CockpitInstantInsight | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setInsight(null);

    getCockpitInsight(caseId)
      .then((data) => {
        if (!cancelled) setInsight(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          getApiErrorDetail(err) ??
            "Insight indisponible (acces au dossier ou moteur IA).",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [caseId]);

  if (loading) {
    return (
      <div
        className="flex items-center justify-center gap-2 py-6 text-sm"
        style={{ color: "var(--arbitre-slate-300)" }}
      >
        <Loader2 className="animate-spin" size={16} />
        Generation de l'insight…
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="rounded-md border p-3 text-xs"
        style={{
          borderColor: "color-mix(in oklch, var(--block-red) 40%, transparent)",
          background: "color-mix(in oklch, var(--block-red) 10%, transparent)",
          color: "rgb(254 202 202)",
        }}
      >
        {error}
      </div>
    );
  }

  if (!insight) return null;

  const conf = Math.round(insight.ai_confidence * 100);

  return (
    <div className="space-y-3">
      <header className="flex items-center justify-between">
        <span
          className="gsf-mono-proof text-xs"
          style={{ color: "var(--proof-gold)" }}
        >
          {insight.case_reference}
        </span>
        <span
          className="inline-flex items-center gap-1 text-[11px]"
          style={{
            color: insight.on_track
              ? "var(--proof-gold)"
              : "var(--alert-orange)",
          }}
        >
          {insight.on_track ? (
            <CheckCircle2 size={12} />
          ) : (
            <AlertTriangle size={12} />
          )}
          {insight.on_track ? "Dans les delais" : "Attention requise"}
        </span>
      </header>

      <p
        className="text-sm leading-relaxed"
        style={{ color: "var(--arbitre-slate-100)" }}
      >
        {insight.summary}
      </p>

      <p
        className="text-xs italic"
        style={{ color: "var(--arbitre-slate-300)" }}
      >
        {insight.status_assessment}
      </p>

      <div className="grid grid-cols-3 gap-2">
        <Metric
          label="Ecoules"
          value={`${insight.days_elapsed}j`}
          tone="neutral"
        />
        <Metric
          label="Restants"
          value={`${insight.days_remaining}j`}
          tone={insight.days_remaining <= 14 ? "warn" : "ok"}
        />
        <Metric
          label="Progression"
          value={`${insight.progress_percentage.toFixed(0)}%`}
          tone="info"
        />
      </div>

      {insight.key_points.length > 0 && (
        <Section title="Points cles">
          <ul
            className="list-disc pl-4 text-xs space-y-1"
            style={{ color: "var(--arbitre-slate-300)" }}
          >
            {insight.key_points.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        </Section>
      )}

      {insight.risk_factors.length > 0 && (
        <Section title="Facteurs de risque" tone="warn">
          <ul
            className="list-disc pl-4 text-xs space-y-1"
            style={{ color: "var(--alert-orange)" }}
          >
            {insight.risk_factors.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </Section>
      )}

      {insight.next_steps.length > 0 && (
        <Section title="Prochaines etapes">
          <ol
            className="text-xs space-y-1"
            style={{ color: "var(--arbitre-slate-300)" }}
          >
            {insight.next_steps.map((s, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <ArrowRight
                  size={11}
                  className="mt-0.5 shrink-0"
                  style={{ color: "var(--proof-gold)" }}
                />
                <span>{s}</span>
              </li>
            ))}
          </ol>
        </Section>
      )}

      <div
        className="flex items-center justify-between rounded-md p-2 text-[11px]"
        style={{
          background: "color-mix(in oklch, var(--ai-violet) 8%, transparent)",
          color: "var(--arbitre-slate-300)",
        }}
      >
        <span className="inline-flex items-center gap-1">
          <Sparkles size={11} style={{ color: "var(--ai-violet)" }} />
          Genere par IA — confiance {conf}%
        </span>
        <span style={{ color: "var(--arbitre-slate-500)" }}>
          {new Date(insight.generated_at).toLocaleString("fr-FR")}
        </span>
      </div>

      {insight.ai_sources.length > 0 && (
        <details>
          <summary
            className="text-[11px] cursor-pointer"
            style={{ color: "var(--arbitre-slate-500)" }}
          >
            Sources ({insight.ai_sources.length})
          </summary>
          <ul
            className="mt-1 list-disc pl-4 text-[10px]"
            style={{ color: "var(--arbitre-slate-500)" }}
          >
            {insight.ai_sources.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "neutral" | "ok" | "warn" | "info";
}) {
  const color =
    tone === "warn"
      ? "var(--alert-orange)"
      : tone === "ok"
        ? "var(--proof-gold)"
        : tone === "info"
          ? "var(--ai-violet)"
          : "var(--arbitre-slate-300)";
  return (
    <div
      className="rounded-md p-2 text-center"
      style={{ background: "var(--graphite-700)" }}
    >
      <div
        className="text-caption-arbitre"
        style={{ color: "var(--arbitre-slate-500)" }}
      >
        {label}
      </div>
      <div className="text-lg font-bold" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

function Section({
  title,
  children,
  tone = "neutral",
}: {
  title: string;
  children: React.ReactNode;
  tone?: "neutral" | "warn";
}) {
  const color =
    tone === "warn" ? "var(--alert-orange)" : "var(--proof-gold)";
  return (
    <section>
      <h4
        className="text-caption-arbitre mb-1"
        style={{ color }}
      >
        {title}
      </h4>
      {children}
    </section>
  );
}
